import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ChangeEvent, ClipboardEvent, DragEvent, KeyboardEvent } from 'react';
import type { EmojiClickData } from 'emoji-picker-react';
import { FolderOpen, HardDrive, Loader2, Paperclip, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import EmojiPickerPopover from '@/components/lobby/EmojiPickerPopover';
import InputAttachmentPreview from '@/components/chat/InputAttachmentPreview';
import MapleOSFilePickerDialog from '@/components/chat/MapleOSFilePickerDialog';
import { useAuth } from '@/contexts/AuthContext';
import { useUserMessagesContext } from '@/contexts/UserMessagesContext';
import { supabase } from '@/integrations/supabase/client';
import { globalVFS } from '@/OS/utils/storage/VFS';
import type { FileMetadata } from '@/OS/utils/storage/StorageDriver';
import type { Attachment } from '@/types';
import type { UserMessageAttachment } from '@/hooks/useUserMessages';

const sanitizeFileName = (name: string) => name.replace(/[^a-zA-Z0-9._-]/g, '-');
const imageExtensions = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico']);

const getExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() || '';
const isHttpUrl = (value: string) => /^https?:\/\//i.test(value.trim());

const inferMimeType = (fileName: string) => {
  const extension = getExtension(fileName);

  switch (extension) {
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'gif':
      return 'image/gif';
    case 'webp':
      return 'image/webp';
    case 'svg':
      return 'image/svg+xml';
    case 'bmp':
      return 'image/bmp';
    case 'ico':
      return 'image/x-icon';
    case 'pdf':
      return 'application/pdf';
    case 'json':
      return 'application/json';
    case 'csv':
      return 'text/csv';
    case 'md':
      return 'text/markdown';
    case 'txt':
    default:
      return 'text/plain';
  }
};

const createAttachmentFromFile = (file: File) =>
  new Promise<Attachment>((resolve, reject) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({
          id: crypto.randomUUID(),
          type: 'image',
          content: String(reader.result || ''),
          name: file.name,
        });
      };
      reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
      reader.readAsDataURL(file);
      return;
    }

    resolve({
      id: crypto.randomUUID(),
      type: 'file',
      content: '',
      name: file.name,
    });
  });

const createMapleOsUploadFile = async (fileName: string, content: string) => {
  const trimmedContent = content.trim();

  if (trimmedContent.startsWith('data:') || isHttpUrl(trimmedContent)) {
    const response = await fetch(trimmedContent);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${fileName}`);
    }

    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type || inferMimeType(fileName) });
  }

  return new File([content], fileName, { type: inferMimeType(fileName) });
};

const createAttachmentFromMapleOsFile = (file: FileMetadata, content: string): Attachment => {
  const fileName = file.path.split('\\').pop() || file.path;
  const extension = getExtension(fileName);
  const trimmedContent = content.trim();

  if (imageExtensions.has(extension)) {
    if (content.startsWith('data:image/')) {
      return {
        id: crypto.randomUUID(),
        type: 'image',
        content,
        name: fileName,
      };
    }

    if (isHttpUrl(trimmedContent)) {
      return {
        id: crypto.randomUUID(),
        type: 'image',
        content: trimmedContent,
        name: fileName,
      };
    }

    if (extension === 'svg' && trimmedContent.startsWith('<svg')) {
      return {
        id: crypto.randomUUID(),
        type: 'image',
        content: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(content)}`,
        name: fileName,
      };
    }
  }

  return {
    id: crypto.randomUUID(),
    type: 'file',
    content: '',
    name: fileName,
  };
};

export interface MessageInputDMHandle {
  openFilePicker: () => void;
}

interface MessageInputDMProps {
  recipientId: string;
  disabled?: boolean;
  placeholder?: string;
  setSelfTyping: (isTyping: boolean) => void | Promise<void>;
}

const MessageInputDM = forwardRef<MessageInputDMHandle, MessageInputDMProps>(function MessageInputDM(
  {
    recipientId,
    disabled = false,
    placeholder = 'Type a message...',
    setSelfTyping,
  },
  ref
) {
  const { user } = useAuth();
  const { sendMessage } = useUserMessagesContext();
  const { toast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimerRef = useRef<number | null>(null);
  const [messageText, setMessageText] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);
  const [isMapleOsPickerOpen, setIsMapleOsPickerOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    openFilePicker: () => setIsAttachMenuOpen(true),
  }));

  const canSend = useMemo(() => {
    return !disabled && !isUploading && (messageText.trim().length > 0 || attachments.length > 0);
  }, [attachments.length, disabled, isUploading, messageText]);

  const stopTypingSoon = () => {
    if (typingTimerRef.current) {
      window.clearTimeout(typingTimerRef.current);
    }

    typingTimerRef.current = window.setTimeout(() => {
      void setSelfTyping(false);
      typingTimerRef.current = null;
    }, 1500);
  };

  const announceTyping = () => {
    void setSelfTyping(true);
    stopTypingSoon();
  };

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        window.clearTimeout(typingTimerRef.current);
      }
      void setSelfTyping(false);
    };
  }, [setSelfTyping]);

  const addFiles = async (files: FileList | File[]) => {
    const nextFiles = Array.from(files);
    if (nextFiles.length === 0) {
      return;
    }

    try {
      const nextAttachments = await Promise.all(nextFiles.map(createAttachmentFromFile));
      const nextFileMap = nextAttachments.reduce<Record<string, File>>((accumulator, attachment, index) => {
        accumulator[attachment.id] = nextFiles[index];
        return accumulator;
      }, {});

      setAttachments((current) => [...current, ...nextAttachments]);
      setPendingFiles((current) => ({ ...current, ...nextFileMap }));
    } catch {
      toast({
        title: 'Attachment error',
        description: 'One of the selected files could not be prepared.',
        variant: 'destructive',
      });
    }
  };

  const handleNativeFilePickerOpen = () => {
    setIsAttachMenuOpen(false);
    fileInputRef.current?.click();
  };

  const handleMapleOsPickerOpen = () => {
    setIsAttachMenuOpen(false);
    setIsMapleOsPickerOpen(true);
  };

  const handleFilePickerChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return;
    }

    await addFiles(event.target.files);
    event.target.value = '';
  };

  const handleMapleOsFileSelect = async (file: FileMetadata) => {
    try {
      const content = await globalVFS.load(file.path);
      if (content === null) {
        throw new Error('Unable to load the selected MapleOS file.');
      }

      const attachment = createAttachmentFromMapleOsFile(file, content);
      const uploadFile = await createMapleOsUploadFile(attachment.name || file.path.split('\\').pop() || 'attachment', content);

      setAttachments((current) => [...current, attachment]);
      setPendingFiles((current) => ({ ...current, [attachment.id]: uploadFile }));
      setIsMapleOsPickerOpen(false);
    } catch {
      toast({
        title: 'Attachment error',
        description: 'The selected MapleOS file could not be prepared.',
        variant: 'destructive',
      });
    }
  };

  const handlePaste = async (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData.files || []);
    if (files.length === 0) {
      return;
    }

    event.preventDefault();
    await addFiles(files);
  };

  const handleDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDraggingOver(false);

    const files = Array.from(event.dataTransfer.files || []);
    if (files.length === 0) {
      return;
    }

    await addFiles(files);
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((current) => current.filter((attachment) => attachment.id !== id));
    setPendingFiles((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
  };

  const uploadPendingFiles = async () => {
    if (!user) {
      return [] as UserMessageAttachment[];
    }

    const entries = Object.entries(pendingFiles);
    if (entries.length === 0) {
      return [] as UserMessageAttachment[];
    }

    return Promise.all(
      entries.map(async ([attachmentId, file]) => {
        const safeFileName = sanitizeFileName(file.name || 'attachment');
        const filePath = `${user.id}/${Date.now()}-${attachmentId}-${safeFileName}`;
        const { data, error } = await supabase.storage
          .from('dm-uploads')
          .upload(filePath, file, { upsert: false, contentType: file.type || undefined });

        if (error) {
          throw error;
        }

        const publicUrl = supabase.storage.from('dm-uploads').getPublicUrl(data.path).data.publicUrl;

        return {
          id: attachmentId,
          kind: file.type.startsWith('image/') ? 'image' : 'file',
          name: file.name,
          url: publicUrl,
          mime_type: file.type || undefined,
        } satisfies UserMessageAttachment;
      })
    );
  };

  const handleSubmit = async () => {
    if (!user || !recipientId || !canSend) {
      return;
    }

    setIsUploading(true);

    try {
      const uploadedAttachments = await uploadPendingFiles();
      const result = await sendMessage(recipientId, messageText.trim(), uploadedAttachments);

      if (!result) {
        return;
      }

      setMessageText('');
      setAttachments([]);
      setPendingFiles({});
      await setSelfTyping(false);
      if (typingTimerRef.current) {
        window.clearTimeout(typingTimerRef.current);
        typingTimerRef.current = null;
      }
      textareaRef.current?.focus();
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error?.message || 'Unable to send this message right now.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyDown = async (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      await handleSubmit();
    }
  };

  const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setMessageText(event.target.value);
    announceTyping();
  };

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    const currentValue = messageText;
    const selectionStart = textareaRef.current?.selectionStart ?? currentValue.length;
    const selectionEnd = textareaRef.current?.selectionEnd ?? currentValue.length;
    const nextValue =
      currentValue.slice(0, selectionStart) + emojiData.emoji + currentValue.slice(selectionEnd);

    setMessageText(nextValue);
    announceTyping();

    window.setTimeout(() => {
      const nextCursorPosition = selectionStart + emojiData.emoji.length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(nextCursorPosition, nextCursorPosition);
    }, 0);
  };

  const hasAttachments = attachments.length > 0;

  return (
    <>
      <div
        className={`${hasAttachments ? 'rounded-2xl border bg-card' : 'rounded-xl border bg-card/80'} ${isDraggingOver ? 'border-primary bg-accent/30' : 'border-border/70'}`}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDraggingOver(true);
        }}
        onDragLeave={(event) => {
          if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
            return;
          }
          setIsDraggingOver(false);
        }}
        onDrop={handleDrop}
      >
        {hasAttachments && (
          <InputAttachmentPreview attachments={attachments} onRemoveAttachment={handleRemoveAttachment} />
        )}
        <div className="flex items-center gap-1.5 p-1.5">
          <DropdownMenu open={isAttachMenuOpen} onOpenChange={setIsAttachMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8.5 w-8.5"
                disabled={disabled || isUploading}
              >
                <Paperclip className="h-4 w-4" />
                <span className="sr-only">Attach files</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-52">
              <DropdownMenuItem onClick={handleNativeFilePickerOpen} disabled={disabled || isUploading}>
                <HardDrive className="mr-2 h-4 w-4" />
                <span>Attach from Native OS</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleMapleOsPickerOpen} disabled={disabled || isUploading}>
                <FolderOpen className="mr-2 h-4 w-4" />
                <span>Attach from MapleOS</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFilePickerChange}
          />

          <EmojiPickerPopover onEmojiSelect={handleEmojiSelect} />

          <Textarea
            ref={textareaRef}
            value={messageText}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            disabled={disabled || isUploading}
            rows={1}
            className="min-h-[38px] max-h-28 flex-1 resize-none px-3 py-2"
            aria-label="Message composer"
          />

          <Button
            type="button"
            size="icon"
            className="h-9 w-9"
            disabled={!canSend}
            onClick={handleSubmit}
          >
            {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            <span className="sr-only">Send message</span>
          </Button>
        </div>
      </div>

      <MapleOSFilePickerDialog
        open={isMapleOsPickerOpen}
        onOpenChange={setIsMapleOsPickerOpen}
        onSelectFile={(file) => void handleMapleOsFileSelect(file)}
        selecting={isUploading}
      />
    </>
  );
});

export default MessageInputDM;